import {
  fetchGet,
  fetchPost,
  fetchPut,
  toQueryString,
} from "../../../../service/api_client";

export type BulkUploadType =
  | "FG_STOCK_IN"
  | "FG_STOCK_OUT"
  | "RAW_MATERIAL_UPLOAD"
  | "ITEM_CODE_TRANSFER"
  | "SCAN_CODE_DEVICES_FETCH";

type StockUploadType = Exclude<
  BulkUploadType,
  "SCAN_CODE_DEVICES_FETCH" | "ITEM_CODE_TRANSFER"
>;

export interface BulkStockRow {
  item_code: string;
  quantity: number;
}

export interface BulkStockUploadPayload {
  type: StockUploadType;
  file: File;
  warehouse_id: string;
  item_code?: string;
  date: string;
  remarks: string;
}

export interface ItemCodeTransferPayload {
  file: File;
  warehouse_id: string;
  item_code: string;
  to_item_code: string;
  remarks?: string;
}

export interface ExecuteTransformPayload {
  file: File;
  warehouse_id: string;
  transform_id: string;
  source_item_code: string;
  remarks?: string;
}

// Action values written to inventory_audit_stock_logs by the four bulk flows.
// Mirrors InventoryAuditAction in kazam-backend.
export const InventoryAuditAction = {
  device_stock_inward: "Device Stock Inward",
  device_stock_outward: "Device Stock Outward",
  rm_stock_update: "RM Stock Update",
  item_code_transfer: "Item Code Transfer",
} as const;

export interface InventoryAuditDeviceMovement {
  item_code?: string;
  movement_type?: string;
  qty?: number;
  created_at?: string;
}

// Per-item outcome of the WAC append that ran with the action. Only unclean or
// noteworthy outcomes are recorded by the backend.
export interface InventoryAuditWacNote {
  item_code: string;
  qty: number;
  action: "Inward" | "Outward";
  status:
    | "recorded"
    | "bootstrapped"
    | "revalued"
    | "clamped"
    | "skipped_no_wac"
    | "skipped_no_rate";
  wac_value?: number;
  message?: string;
}

export interface InventoryAuditStockLog {
  id: string;
  action: string;
  warehouse_id: string;
  warehouse_name?: string;
  item_code?: string;
  to_item_code?: string;
  date?: string;
  remarks?: string;
  file_name?: string;
  file_url?: string;
  unique_ids?: string[];
  records_count: number;
  status: string;
  wac_notes?: InventoryAuditWacNote[];
  created_by_name?: string;
  created_at?: string;
  device_movement_ids?: string[];
  device_movements?: InventoryAuditDeviceMovement[];
}

export interface InventoryAuditStockLogFilter {
  page: number;
  count: number;
  action?: string;
  warehouse_id?: string;
  item_code?: string;
  from_date?: string;
  to_date?: string;
}

// Read-only listing of inventory_audit_stock_logs. Expected response shape
// (matching the backend $facet pagination used elsewhere):
//   { success, data: { logs: InventoryAuditStockLog[],
//                       metadata: [{ total, page, count }] } }
export const FetchInventoryAuditStockLogsApi = async (
  filter: InventoryAuditStockLogFilter,
) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/stocks/inventory-audit-stock-logs`;
  const res = await fetchGet(url + toQueryString(filter));
  return res;
};

// Loads the per-movement breakdown for a single audit log on demand (when a row
// is expanded). The list endpoint no longer joins device_movements.
export const FetchInventoryAuditLogMovementsApi = async (logId: string) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/stocks/inventory-audit-stock-logs/${logId}/movements`;
  const res = await fetchGet(url);
  return res;
};

export interface ScanCodeDevice {
  id: string;
  box_name: string;
  device_id: string;
  item_code: string;
}

// Upload a CSV of Scan IDs; backend parses the file and returns the matching
// device list.
export const FetchDevicesByScanCodesApi = async (file: File) => {
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/stocks/device-list-from-scan-code`;
  const fd = new FormData();
  fd.append("file", file, file.name);
  const res = await fetchPost(url, fd);
  return res;
};

// ===========================================================================
// Maker/checker stock-action requests
// ===========================================================================

// Maps the UI's BulkUploadType to the backend `action` value (InventoryAuditAction).
export const StockRequestAction: Record<
  Exclude<BulkUploadType, "SCAN_CODE_DEVICES_FETCH">,
  string
> = {
  FG_STOCK_IN: InventoryAuditAction.device_stock_inward,
  FG_STOCK_OUT: InventoryAuditAction.device_stock_outward,
  RAW_MATERIAL_UPLOAD: InventoryAuditAction.rm_stock_update,
  ITEM_CODE_TRANSFER: InventoryAuditAction.item_code_transfer,
};

// Reverse of StockRequestAction: backend `action` value -> UI BulkUploadType.
export const BulkTypeByAction: Record<
  string,
  Exclude<BulkUploadType, "SCAN_CODE_DEVICES_FETCH">
> = {
  [InventoryAuditAction.device_stock_inward]: "FG_STOCK_IN",
  [InventoryAuditAction.device_stock_outward]: "FG_STOCK_OUT",
  [InventoryAuditAction.rm_stock_update]: "RAW_MATERIAL_UPLOAD",
  [InventoryAuditAction.item_code_transfer]: "ITEM_CODE_TRANSFER",
};

export const StockRequestStatus = {
  pending: "Pending Approval",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
} as const;

export interface PreviewIssue {
  type: string;
  message: string;
  items?: any[];
}

export interface PreviewRowDisposition {
  key: string;
  outcome: "will_apply" | "blocked" | "skipped" | "warning";
  movement_type?: string;
  qty?: number;
  message?: string;
  detail?: Record<string, any>;
}

export interface PreviewCounts {
  total_rows: number;
  will_apply: number;
  blocked: number;
  skipped: number;
  warnings: number;
}
export interface PreviewWacLine {
  item_code: string;
  action: "Inward" | "Outward";
  qty: number;
  rate: number | null;
  rate_source: "item_wac" | "source_item_wac" | "derived_po_price" | "none";
  value: number | null;
  current_wac_value?: number;
  projected_wac_value?: number;
  note?: string;
}

export interface PreviewWacImpact {
  lines: PreviewWacLine[];
  inward_value: number;
  outward_value: number;
  net_value: number;
  unvalued_qty: number;
}

export interface PreviewResult {
  ok: boolean;
  action: string;
  summary: string;
  counts: PreviewCounts;
  blocking_errors: PreviewIssue[];
  warnings: PreviewIssue[];
  rows?: PreviewRowDisposition[];
  wac_impact?: PreviewWacImpact;
  computed_at: string;
}

export interface StockRequestHistoryEntry {
  event: string;
  at: string;
  by?: string;
  by_name?: string;
  reason?: string;
  revision?: number;
}

export interface StockActionRequest {
  id: string;
  action: string;
  status: string;
  warehouse_id: string;
  warehouse_name?: string;
  item_code?: string;
  to_item_code?: string;
  date?: string;
  remarks?: string;
  file_name?: string;
  file_url?: string;
  transform_id?: string;
  source_item_code?: string;
  device_ids?: string[];
  rm_rows?: { item_code: string; qty: number }[];
  preview?: PreviewResult;
  created_by?: string;
  created_by_name?: string;
  created_at?: string;
  approved_by_name?: string;
  approved_at?: string;
  rejected_by_name?: string;
  rejected_at?: string;
  reject_reason?: string;
  revision?: number;
  history?: StockRequestHistoryEntry[];
}

export interface StockActionRequestPayload {
  action: string;
  file?: File;
  warehouse_id: string;
  item_code?: string;
  to_item_code?: string;
  // Transform-master driven Item Code Transfer (goes through maker/checker).
  transform_id?: string;
  source_item_code?: string;
  remarks?: string;
}

export interface StockActionRequestFilter {
  page: number;
  count: number;
  status?: string;
  action?: string;
  warehouse_id?: string;
  item_code?: string;
  mine?: boolean;
  from_date?: string;
  to_date?: string;
}

const STOCK_REQ_BASE = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/stocks/stock-requests`;

const buildStockRequestFormData = (payload: StockActionRequestPayload) => {
  const fd = new FormData();
  if (payload.file) fd.append("file", payload.file, payload.file.name);
  fd.append("action", payload.action);
  fd.append("warehouse_id", payload.warehouse_id);
  if (payload.item_code) fd.append("item_code", payload.item_code);
  if (payload.to_item_code) fd.append("to_item_code", payload.to_item_code);
  if (payload.transform_id) fd.append("transform_id", payload.transform_id);
  if (payload.source_item_code)
    fd.append("source_item_code", payload.source_item_code);
  if (payload.remarks !== undefined)
    fd.append("remarks", payload.remarks ?? "");
  return fd;
};

// Dry-run: returns { success, preview } without persisting anything.
export const PreviewStockActionApi = async (
  payload: StockActionRequestPayload,
) => fetchPost(`${STOCK_REQ_BASE}/preview`, buildStockRequestFormData(payload));

// Raise a new request (only succeeds when the preview is clean).
export const RaiseStockActionApi = async (payload: StockActionRequestPayload) =>
  fetchPost(STOCK_REQ_BASE, buildStockRequestFormData(payload));

// Edit & resubmit a Pending/Rejected request (file optional).
export const UpdateStockActionRequestApi = async (
  id: string,
  payload: StockActionRequestPayload,
) => fetchPut(`${STOCK_REQ_BASE}/${id}`, buildStockRequestFormData(payload));

export const CancelStockActionRequestApi = async (id: string) =>
  fetchPost(`${STOCK_REQ_BASE}/${id}/cancel`, {});

export const ApproveStockActionRequestApi = async (id: string) =>
  fetchPost(`${STOCK_REQ_BASE}/${id}/approve`, {});

export const RejectStockActionRequestApi = async (id: string, reason: string) =>
  fetchPost(`${STOCK_REQ_BASE}/${id}/reject`, { reason });

export const ListStockActionRequestsApi = async (
  filter: StockActionRequestFilter,
) => fetchGet(STOCK_REQ_BASE + toQueryString(filter));

export const GetStockActionRequestApi = async (id: string) =>
  fetchGet(`${STOCK_REQ_BASE}/${id}`);
